import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Send, CheckCircle2 } from "lucide-react";
import PrivacyConsentCheckbox from "@/components/PrivacyConsentCheckbox";

export default function ContactForm({ propertyId, agencyId, title, defaultMessage }) {
    const { user } = useAuth();
    const isAgencyContact = !propertyId && !!agencyId;
    const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "", message: defaultMessage || "Salve, sono interessato/a a questo immobile. Vorrei avere maggiori informazioni e fissare una visita." });
    const [privacyAccepted, setPrivacyAccepted] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (!privacyAccepted) {
            setError("Devi accettare la Privacy Policy per proseguire.");
            return;
        }
        setSending(true); setError(null);
        try {
            const endpoint = propertyId ? `/properties/${propertyId}/contact` : `/agencies/${agencyId}/contact`;
            await api.post(endpoint, { ...form, privacy_accepted: true });
            setSent(true);
        } catch (err) {
            setError(err.response?.data?.detail || "Errore nell'invio");
        } finally {
            setSending(false);
        }
    };

    if (sent) {
        return (
            <div data-testid="contact-sent" className="border border-gold/40 p-8 bg-ink-900/40 text-center">
                <CheckCircle2 size={32} className="text-gold mx-auto mb-4" />
                <div className="font-serif text-xl text-ink-50 mb-2">Messaggio inviato</div>
                <p className="text-sm text-ink-400">{isAgencyContact ? "L'agenzia riceverà una notifica e ti contatterà a breve." : "Il proprietario riceverà una notifica e ti contatterà a breve."}</p>
            </div>
        );
    }

    const inputCls = "w-full bg-transparent border border-white/15 px-4 py-3 text-ink-50 focus:border-gold outline-none transition-colors text-sm";

    return (
        <form onSubmit={submit} data-testid="contact-form" className="border border-white/10 p-8 bg-ink-900/40 space-y-4">
            <div data-testid="contact-form-title" className="label-eyebrow mb-2">{title || "Contatta il proprietario"}</div>
            <input data-testid="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Nome e cognome" />
            <input data-testid="contact-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="Email" />
            <input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} placeholder="Telefono (opzionale)" />
            <textarea data-testid="contact-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
            <div className="pt-1 border-t border-white/5">
                <PrivacyConsentCheckbox
                    agencyId={agencyId}
                    checked={privacyAccepted}
                    onChange={setPrivacyAccepted}
                    testid="contact"
                />
            </div>
            {error && <div className="text-red-400 text-xs" data-testid="contact-error">{error}</div>}
            <button
                data-testid="contact-submit"
                disabled={sending || !privacyAccepted}
                type="submit"
                className="w-full bg-gold text-black py-3 text-sm font-medium hover:bg-gold-soft transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <Send size={14} /> {sending ? "Invio..." : "Invia messaggio"}
            </button>
        </form>
    );
}
